import { useMemo } from 'react'

import { DescriptionList, SkeletonBox } from '@circlefin/components'
import { useMoney } from '@circlefin/hooks'
import { StatusWithDate } from '@shared/components/common'
import useTranslation from 'next-translate/useTranslation'

import type { ChipProps } from '@circlefin/components/lib/Chip'
import type { TradeStatus, Trade } from '@shared/graphql'

export interface ConvertCardProps {
  /**
   * Convert transaction object.
   */
  convert?: Trade
  /**
   * Display loading state.
   */
  loading?: boolean
}

const STATUS_CHIP_VARIANT_MAP: Record<TradeStatus, ChipProps['variant']> = {
  pending: 'status/neutral',
  pending_confirmed: 'status/neutral',
  pending_settlement: 'status/neutral',
  confirmed: 'status/neutral',
  complete: 'status/success',
  failed: 'status/error',
}

export const ConvertCard: React.FC<ConvertCardProps> = ({
  convert,
  loading,
}) => {
  const { t } = useTranslation('payments')
  const { money } = useMoney()

  const rate = useMemo(() => {
    if (
      !convert?.from.amount ||
      !convert.to.amount ||
      convert.to.currency == null
    )
      return

    return money({
      number: Number(convert.to.amount) / Number(convert.from.amount),
      variant: convert.to.currency,
    })
  }, [convert, money])

  if (loading) {
    return (
      <div className="space-y-3">
        <SkeletonBox className="h-4 w-88" loading={loading} />
        <SkeletonBox className="h-4 w-120" loading={loading} />
        <SkeletonBox className="h-4 w-48" loading={loading} />
      </div>
    )
  }

  if (!convert) return

  return (
    <div className="w-176">
      <p className="type-h-title-lg">{t('convertDetail.transactionDetails')}</p>
      <DescriptionList density="loose">
        <DescriptionList.Label className="w-64">
          {t('convertDetail.type')}
        </DescriptionList.Label>
        <DescriptionList.Description className="type-body-base-bold">
          {t('convertDetail.convert')}
        </DescriptionList.Description>

        <DescriptionList.Label className="w-64">
          {t('convertDetail.status')}
        </DescriptionList.Label>
        <DescriptionList.Description>
          <StatusWithDate
            chipVariant={STATUS_CHIP_VARIANT_MAP[convert.status]}
            date={convert.updateDate}
            status={t(`convertDetail.statusLabel.${convert.status}`)}
          />
        </DescriptionList.Description>

        <DescriptionList.Label className="w-64">
          {t('convertDetail.fromAmount')}
        </DescriptionList.Label>
        <DescriptionList.Description className="type-body-base-bold">
          {convert.from.amount &&
            convert.from.currency != null &&
            money({
              number: convert.from.amount,
              variant: convert.from.currency,
            })}
        </DescriptionList.Description>

        <DescriptionList.Label className="w-64">
          {t('convertDetail.toAmount')}
        </DescriptionList.Label>
        <DescriptionList.Description className="type-body-base-bold">
          {convert.to.amount &&
            convert.to.currency != null &&
            money({
              number: convert.to.amount,
              variant: convert.to.currency,
            })}
        </DescriptionList.Description>

        <DescriptionList.Label className="w-64">
          {t('convertDetail.rate')}
        </DescriptionList.Label>
        <DescriptionList.Description className="type-body-base-bold">
          {t('convertDetail.convertRate', {
            fromCurrency: convert.from.currency,
            rate,
          })}
        </DescriptionList.Description>
      </DescriptionList>
    </div>
  )
}
